import React from 'react';

const GenericTableAnalytics = ({ dashboardData }) => {
  // Get all fields from dashboard_fields
  const allFields = dashboardData?.dashboard_fields?.[0]?.fields || [];
  
  // Filter out fields that are already handled by specific components or should be excluded
  const genericFields = allFields.filter(field => 
    field.label !== 'University attended' && 
    field.label !== 'Languages spoken' &&
    field.label !== 'Gender' &&
    field.label !== 'Disability' &&
    field.isChecked &&
    !field.visualization_type // Exclude fields with specific visualization types
  );

  // Function to extract individual values from field data
  const extractValues = (field) => {
    const fieldData = dashboardData?.field_data?.[field.label];
    
    if (!fieldData) return [];

    // Handle array data
    if (Array.isArray(fieldData)) {
      // Extract values from objects if they have a 'value' property
      return fieldData.map(item => {
        if (typeof item === 'object' && item !== null) {
          // Handle objects with 'value' property (e.g., {value: "text", count: 5})
          if (item.value !== undefined) {
            return item.value;
          }
          // Handle objects with 'university' property (e.g., {university: "MIT", candidate_count: 10})
          if (item.university !== undefined) {
            return item.university;
          }
          // Handle objects with 'language' property (e.g., {language: "English", candidate_count: 10})
          if (item.language !== undefined) {
            return item.language;
          }
          // For other objects, try to get the first string/number value
          const firstValue = Object.values(item).find(val => 
            typeof val === 'string' || typeof val === 'number'
          );
          return firstValue !== undefined ? firstValue : item;
        }
        // If it's already a primitive value, return as-is
        return item;
      });
    }

    // Handle object data by returning its values
    if (typeof fieldData === 'object' && fieldData !== null) {
      return Object.values(fieldData);
    }

    // Handle simple values by wrapping them in an array
    return [fieldData.toString()];
  };

  if (genericFields.length === 0) {
    return null;
  }

  // Group fields into pairs for side-by-side display
  const groupedFields = [];
  for (let i = 0; i < genericFields.length; i += 2) {
    groupedFields.push(genericFields.slice(i, i + 2));
  }

  // Check if there's any data to display
  const hasData = groupedFields.some(fieldPair => 
    fieldPair.some(field => extractValues(field).length > 0)
  );

  if (!hasData) {
    return null;
  }

  return (
    <div className="bg-card rounded-lg shadow-sm">
      <div className="overflow-x-auto p-4">
        {groupedFields.map((fieldPair, pairIndex) => (
          <div key={pairIndex} className="flex mb-6 gap-6">
            {fieldPair.map((field, index) => {
              const fieldValues = extractValues(field);
              // Skip rendering if there's no data for this field
              if (fieldValues.length === 0) {
                return <div key={index} className="flex-1"></div>;
              }
              return (
                <div key={index} className="flex-1 bg-card rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="px-6 py-4 bg-muted border-border">
                    <h3 className="text-xl font-semibold text-foreground">Latest 20 list of {field.label}</h3>
                  </div>
                  <table className="min-w-full divide-y divide-border">
                    <tbody className="bg-card divide-y divide-border">
                      {fieldValues.map((value, valueIndex) => {
                        // Ensure we're rendering a primitive value, not an object
                        const displayValue = typeof value === 'object' && value !== null 
                          ? (value.value !== undefined ? value.value : JSON.stringify(value))
                          : value;
                        
                        return (
                          <tr key={valueIndex} className="hover:bg-muted transition-colors duration-150">
                            <td className="px-6 py-4 text-left text-sm text-muted-foreground break-all border-r">
                              {displayValue === 'Applied' ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                  Applied
                                </span>
                              ) : (
                                String(displayValue)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
            {/* Add empty div if there's only one field in the pair */}
            {fieldPair.length === 1 && <div className="flex-1"></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GenericTableAnalytics; 